"use client";

import CustomCopyButton from "@/app/(core)/profile/performance/Buttons/CustomCopyButton";
import { ContentRenderer } from "@/lib/content/ContentRenderer";
import { tokensToHtml } from "@/lib/content/tokenizer";
import { getUserRole } from "@/lib/utils/accessChecks";
import { userHasAdminRights } from "@/lib/utils/pageChecks";
import { CommandTerm } from "@/types/command-term-schema";
import { Badge, Box, Card, SimpleGrid, Text } from "@mantine/core";
import { Definition } from "@tsTypes/definition-schema";
import { useSession } from "next-auth/react";
import { type ReactNode } from "react";
import ResourceEditInStrapiButton from "../../../questions/[topic]/components/Header/ResourceEditInStrapiButton";
import classes from "./TermsGridLayout.module.css";

export default function TermsGridLayout({
  type,
  definitions,
  subjectColorIndex,
  publishActionByDocumentId,
}: {
  type: "definition" | "command term";
  definitions: Definition[] | CommandTerm[];
  subjectColorIndex: string;
  publishActionByDocumentId?: Record<string, ReactNode | undefined>;
}) {
  const { data: session } = useSession();
  const userRole = getUserRole(session);
  const userHasAdminEditAccess =
    userHasAdminRights(userRole) || userRole === "Content Creator";

  const termVars = {
    "--terms-grid-term-color": `light-dark(var(--mantine-color-${subjectColorIndex}-8), var(--mantine-color-${subjectColorIndex}-4))`,
  } as React.CSSProperties;

  if (definitions.length === 0) {
    return (
      <Text c="dimmed" ta="center" mt="md" mb="xl">
        No {type}s found.
      </Text>
    );
  }

  return (
    <Box className={classes.wrapper}>
      <SimpleGrid
        className="progressive-fade-in"
        cols={{ base: 1, sm: 2, md: 3 }}
        spacing={{ base: "sm", sm: "md", md: "lg" }}
      >
        {definitions.map((term, index) => {
          const definitionText = Array.isArray(term.Definition)
            ? tokensToHtml(term.Definition)
            : term.Definition;
          const copyText = `${term.Term}: ${definitionText}`;
          const isHigherLevel =
            type === "definition" &&
            (term as Definition).RelatedSubTopics?.every(
              (st) => st.IsHigherLevel === true
            );
          return (
            <Card
              key={term.documentId || index}
              p="lg"
              radius="md"
              className={classes.card}
              style={termVars}
            >
              {isHigherLevel && (
                <Badge
                  className={classes.hlChip}
                  color={subjectColorIndex}
                  variant="filled"
                  size="md"
                >
                  HL
                </Badge>
              )}
              <Box className={classes.copyButton}>
                <CustomCopyButton
                  value={copyText}
                  defaultTooltip={`Copy term and definition`}
                  copiedTooltip="Copied!"
                />
              </Box>
              {(userHasAdminEditAccess ||
                publishActionByDocumentId?.[term.documentId]) && (
                <Box className={classes.adminControls}>
                  {publishActionByDocumentId?.[term.documentId]}
                  {userHasAdminEditAccess && (
                    <ResourceEditInStrapiButton
                      resource={
                        type === "definition" ? "definitions" : "command terms"
                      }
                      resourceDocumentId={term.documentId}
                    />
                  )}
                </Box>
              )}
              <Text fw={700} size="lg" className={classes.term}>
                {term.Term}
              </Text>
              <Box className={classes.definition}>
                <ContentRenderer content={term.Definition} />
              </Box>
            </Card>
          );
        })}
      </SimpleGrid>
    </Box>
  );
}
